import { useTrack } from '@hackler/react-sdk'
import { DSBottomSheet, DSTooltip } from '@repo/design-system/index'
import { QIcon_calendar_filled, QIcon_clock, QIcon_coupon, QIcon_question_outline } from '@repo/icons/index'
import classNames from 'classnames'
import { addDays } from 'date-fns'
import React, { useState } from 'react'
import Cookies from 'universal-cookie'
import { sendGTMEvent } from '../../../helpers/gtmHelper'
import { getJwtToken } from '../../../modules/auth'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { COOKIE_DOMAIN } from '../../../modules/envVars'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsInApp from '../../../modules/hooks/common/useIsInApp'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_pricing, n_purchase, s_menu, s_portal_home, s_pricing } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { STUDENT_BTN_ID } from '../../../modules/ids/gtm/student'
import urls from '../../../modules/urls'
import { add24Hours, isFirstBenefitUser } from '../../../modules/utils'
import { COLORS, COUNTRY_ALPHA2_CODE, DOLLARS_TO_KRW, SIGN_UP_MODE } from '../../../modules/vars'
import usePurchaseStore from '../../../store/usePurchaseStore'
import { useBasicPopup } from '../../basic/popup/BasicPopup'
import { D_DAY_TYPE, DdayCounter } from '../../common/DdayCounter'
import { PRICING_TAB } from './PricingBodyWeb.util'
import UpSellingPopupBody from './UpSellingPopupBody'
import { HARD_CORDED_B2B_DISCOUNT_DATA } from './data'

type ProductCardPropsType = {
  item: any
  duration: any
  lessonNum: any
  packageOption: any
  isMobile: boolean
  setShowProductCardDetailPopup?: any
  discount: boolean
  activeId?: string
  expiredAt?: any
  isNewVersion?: boolean
  productsGroup?: any
  countryCode?: any
  enableUpsellingPopup?: boolean
  refetchProductLine?: () => void
  isVarationA?: boolean
}

export const NormalDetailContent = ({ item }) => {
  const { t } = useTranslation()

  const getDelayCount = () => {
    if (item.coupon_count == SECOND_CREDIT_COUNT) {
      return 1
    } else if (item.coupon_count == THIRD_CREDIT_COUNT) {
      return 1
    } else if (item.coupon_count == LAST_CREDIT_COUNT) {
      return 3
    } else {
      return null
    }
  }

  const conversationCreditCounts = item?.bonuses?.conversation_credit_bonus || 0 // AI 스피킹 파트너 수업권 제공 갯수

  // B2B완강시 제공되는 추가 AI 스피킹 파트너 수업권 제공 횟수
  const completionConversationCreditBonusCount = item?.bonuses?.completion_conversation_credit_bonus || 0
  const deductionGraceCount = item?.bonuses?.deduction_grace_count || 0 // 수업 연기 갯수

  const defaultBenefitList = [t(n_pricing.benefit.ai), t(n_pricing.benefit.feedback), t(n_pricing.benefit.script)]

  const isMarginExist =
    (item.is_weekly_lesson_package && Boolean(getDelayCount())) ||
    item?.include_booking_priority_benefit ||
    conversationCreditCounts > 0

  return (
    <div className=" flex flex-col">
      <div className="flex gap-y-[8px] flex-col">
        {/* 스터디 클럽 */}
        {item?.bonuses.study_club_benefit && (
          <div className="Body2-14Bd flex items-center text-gray-800 relative">
            <div className="whitespace-nowrap">{t(n_pricing.summer_24.benefit_1)}</div>
            <div className="absolute top-[0px] right-[0px]">
              <DSTooltip
                place={'top'}
                isOpenOnClick={true}
                isMultiline={true}
                triggerComponent={
                  <QIcon_question_outline color={COLORS.GRAY500} className="ml-[4px]" width={20} height={20} />
                }
                contentComponent={t(n_pricing.summer_24.benefit_1_tooltip)}
              />
            </div>
          </div>
        )}

        {/* 수업 홀딩 */}
        {item?.is_weekly_lesson_package && item?.properties.hold_activation && (
          <div className="Body2-14Bd flex items-center text-gray-800 relative">
            <div className="whitespace-nowrap">{t(n_pricing.summer_24.benefit_hold)}</div>
            <div className="absolute top-[0px] right-[0px]">
              <DSTooltip
                place={'top'}
                isOpenOnClick={true}
                isMultiline={true}
                triggerComponent={
                  <QIcon_question_outline color={COLORS.GRAY500} className="ml-[4px]" width={20} height={20} />
                }
                contentComponent={t(n_pricing.summer_24.benefit_hold_tooltip)}
              />
            </div>
          </div>
        )}

        {/* AI 스피킹 파트너 */}
        {conversationCreditCounts > 0 && (
          <div className="Body2-14Bd flex items-center text-gray-800 relative">
            <div className="whitespace-nowrap">
              {t(
                n_pricing.pay_benefit_ai.normal(
                  completionConversationCreditBonusCount > 0
                    ? `${conversationCreditCounts}+${completionConversationCreditBonusCount}`
                    : conversationCreditCounts
                )
              )}
            </div>
            <div className="absolute top-[0px] right-[0px]">
              <DSTooltip
                place={'top'}
                isOpenOnClick={true}
                isMultiline={true}
                triggerComponent={
                  <QIcon_question_outline color={COLORS.GRAY500} className="ml-[4px]" width={20} height={20} />
                }
                contentComponent={t(
                  completionConversationCreditBonusCount > 0
                    ? n_pricing.benefit_b2b2c.bonus_tooltip
                    : n_pricing.pay_benefit_ai.tooltip
                )}
              />
            </div>
          </div>
        )}

        {/* 수업연기 횟수가 존재하는 경우에 수업연기 (n회) 추가 */}
        {item.is_weekly_lesson_package && deductionGraceCount > 0 && (
          <div className="Body2-14Bd flex items-center text-gray-800 relative">
            <div className="whitespace-nowrap">{t(s_pricing.benefit.delay(deductionGraceCount))}</div>
            <div className="absolute top-[0px] right-[0px]">
              <DSTooltip
                place={'top'}
                isOpenOnClick={true}
                isMultiline={true}
                triggerComponent={
                  <QIcon_question_outline color={COLORS.GRAY500} className="ml-[4px]" width={20} height={20} />
                }
                contentComponent={t(s_pricing.benefit.delay_desc)}
              />
            </div>
          </div>
        )}

        {/* 튜터 우선 예약 */}
        {item?.include_booking_priority_benefit && (
          <div className="Body2-14Bd flex items-center text-gray-800 relative">
            <div>{t(n_pricing.benefit.priority_booking)}</div>
            <div className="absolute top-[0px] right-[0px]">
              <DSTooltip
                place={'top'}
                isOpenOnClick={true}
                isMultiline={true}
                triggerComponent={
                  <QIcon_question_outline color={COLORS.GRAY500} className="ml-[4px]" width={20} height={20} />
                }
                contentComponent={t(s_pricing.benefit.first_booking_desc)}
              />
            </div>
          </div>
        )}
      </div>
      {/* 디폴트 혜택 리스트 4종 defaultBenefitList */}
      <div
        className={classNames('mb-[8px] flex flex-col gap-y-[8px]', {
          'mt-[16px]': isMarginExist,
        })}
      >
        {defaultBenefitList.map((item, i) => {
          return (
            <div className="Body2-14Md flex items-center text-gray-400 whitespace-nowrap" key={i}>
              <div>{item}</div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const FIRST_CREDIT_COUNT = 4
export const SECOND_CREDIT_COUNT = 12
export const THIRD_CREDIT_COUNT = 24
export const LAST_CREDIT_COUNT = 48

const ProductCard: React.FC<ProductCardPropsType> = ({
  item,
  duration,
  lessonNum,
  packageOption,
  isMobile,
  discount,
  activeId,
  expiredAt,
  isNewVersion,
  productsGroup,
  countryCode,
  enableUpsellingPopup = true,
  isVarationA = true,
  refetchProductLine,
}) => {
  const { t, isLocaleKO } = useTranslation()
  const { currentUser } = useCurrentUser()
  const getFullUrl = useReturnFullUrl()
  const hackleTrack = useTrack()
  const basicPopup = useBasicPopup()
  const router = useAppRouter()
  const cookies = new Cookies()

  const convertedCountryCode = countryCode == COUNTRY_ALPHA2_CODE.KR ? countryCode : COUNTRY_ALPHA2_CODE.US
  const salesCoupon = item?.properties?.is_popular || false // 인기 라벨 여부
  const isApp = router.query.app ? true : false // 앱에서 여부
  const hasAvailableCoupon =
    item && item?.available_discount_coupon != null && item?.available_discount_coupon?.amount?.value > 0 // 업셀링 팝업을 통해서 얻은 쿠폰이 있는지 여부
  const deductionGraceCount = item?.bonuses?.deduction_grace_count || 0 // 수업 연기 갯수

  const isUpsellingPopupCredit = item.coupon_count == FIRST_CREDIT_COUNT

  const firstPurchaseBenefitCount = isFirstBenefitUser(currentUser) ? item.first_purchase_benefit_count : 0 //첫 구매 혜택 추가 수업권 갯수
  const completionBenefitCount = item?.bonuses?.completion_bonus || 0 // B2B2C 완강 보너스 수업권 추가 갯수
  const conversationCreditCounts = item?.bonuses?.conversation_credit_bonus || 0 // AI 스피킹 파트너 수업권 제공 갯수
  const completionConversationCreditBonusCount = item?.bonuses?.completion_conversation_credit_bonus || 0 // B2B완강시 제공되는 추가 AI 스피킹 파트너 수업권 제공 횟수

  const [openUpSellingBottomSheet, setOpenUpSellingBottomSheet] = useState<boolean>(false)

  const setAvailablePoint = usePurchaseStore((state) => state.setAvailablePoint)

  const isWebview = useIsWebview()
  const isInApp = useIsInApp()

  const handlePurchase = (e) => {
    e.stopPropagation()

    const isOnceClosedUpsellingPopup = cookies.get(`upselling_popup_${currentUser?.id}`)

    setAvailablePoint(discount ? item.available_point.value : 0)
    if (!getJwtToken()) {
      const url = `${urls.student.auth.new}?mode=${SIGN_UP_MODE.PURCHASE}&productID=${item.id}`

      if (isWebview && !isInApp) {
        router.push(url, {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: { url: getFullUrl(url) },
        })
      } else {
        router.push(url)
      }
      //업셀링 24.04.29
    } else {
      if (isOnceClosedUpsellingPopup || !isUpsellingPopupCredit || !enableUpsellingPopup) {
        const url = isNewVersion
          ? `${urls.student.newPortal.pricing.detail(item.id)}?country_code=${convertedCountryCode}&app=${isApp}`
          : `${urls.student.newPortal.pricing.detail(
              item.id
            )}?country_code=${convertedCountryCode}&app=${isApp}&version=old`

        if (isWebview && !isInApp) {
          router.push(url, {
            appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
            appSendData: { url: getFullUrl(url) },
          })
        } else {
          router.push(url)
        }

        return
      }

      cookies.set(`upselling_popup_${currentUser?.id}`, '1', {
        domain: COOKIE_DOMAIN,
        path: '/',
        expires: addDays(new Date(), 1),
      })

      if (isMobile) {
        setOpenUpSellingBottomSheet(true)
      } else {
        basicPopup.show({
          id: `upselling_popup`,
          maxW: 464,
          bodyPx: 32,
          noTitle: true,
          bodyPb: 32,
          body: (
            <UpSellingPopupBody
              countryCode={countryCode}
              selectedItem={item}
              productsGroup={productsGroup}
              refetch={refetchProductLine}
            />
          ),
        })
      }
    }
  }

  const getPromotionGapWonLabel = (amount) => {
    const discountWonKo = amount / 10000 // 만원 단위
    const discountWonEn = amount / 1000 // 천원 단위
    const discountDollar = amount
    if (convertedCountryCode === COUNTRY_ALPHA2_CODE.KR) {
      if (isLocaleKO) {
        return t(n_pricing.label.promo_amount_won(discountWonKo + '만원'))
      } else {
        return t(n_pricing.label.promo_amount_won(discountWonEn))
      }
    } else {
      return t(n_pricing.label.promo_amount_usd((isLocaleKO ? '$' : '') + discountDollar))
    }
  }

  // 추가 할인 금액을 리턴하는 함수
  function getAdditionalDiscount(item) {
    const product = HARD_CORDED_B2B_DISCOUNT_DATA.find((p) => p.product_line_id === item.id)

    return product?.additional_discount_amount > 0
      ? countryCode == COUNTRY_ALPHA2_CODE.KR
        ? (product?.additional_discount_amount / 10000).toFixed(1) + '만원'
        : '$' + (product?.additional_discount_amount / DOLLARS_TO_KRW).toFixed(1)
      : null // 일치하는 경우 할인 금액 반환, 없으면 null 반환
  }

  // 추가 완강 보너스를 리턴하는 함수
  function getCompletionBonus(item) {
    const product = HARD_CORDED_B2B_DISCOUNT_DATA.find((p) => p.product_line_id === item.id)
    return product ? product?.additional_complete_lesson_credit : null // 일치하는 경우 완강 보너스 반환, 없으면 null 반환
  }

  // .__   __.  _______ ____    __    ____    ____    ____  _______ .______           _______. __    ______   .__   __.
  // |  \ |  | |   ____|\   \  /  \  /   /    \   \  /   / |   ____||   _  \         /       ||  |  /  __  \  |  \ |  |
  // |   \|  | |  |__    \   \/    \/   /      \   \/   /  |  |__   |  |_)  |       |   (----`|  | |  |  |  | |   \|  |
  // |  . `  | |   __|    \            /        \      /   |   __|  |      /         \   \    |  | |  |  |  | |  . `  |
  // |  |\   | |  |____    \    /\    /          \    /    |  |____ |  |\  \----..----)   |   |  | |  `--'  | |  |\   |
  // |__| \__| |_______|    \__/  \__/            \__/     |_______|| _| `._____||_______/    |__|  \______/  |__| \__|
  if (isNewVersion) {
    return (
      <div
        key={`${item.id}`}
        className={classNames('relative rounded-[8px]  bg-white drop-shadow-[0px_1px_4px_rgba(0,0,0,0.15)]')}
      >
        {/* 인기 */}
        {salesCoupon && (
          <div className={'absolute w-full rounded-t-[8px] text-center bg-purple-500 py-[2px]'}>
            <div className="Body2-14Md text-white">{t(s_pricing.card_popular)}</div>
          </div>
        )}
        <div className="relative min-h-[542px]">
          <div className="relative flex-col px-[16px] pt-[30px] flex min-w-[280px] h-full">
            <div className={classNames('relative flex flex-col')}>
              <div className="flex mt-[47px] relative">
                {/* N주 패키지 */}
                <div className={'Headline2-24Bd relative flex text-black items-center h-[34px]'}>{item?.name}</div>
                <div className="absolute top-[-32px] flex flex-row gap-x-[6px] flex-nowrap">
                  {/* N원 쿠폰 적용가 */}
                  {item?.available_discount_coupon?.amount?.formatted && (
                    <div className="whitespace-nowrap px-[6px] rounded-[4px] bg-blue-50 text-blue-500 Caption-12Bd border-blue-200 border-1 flex items-center justify-center pt-[1px]">
                      <div className="mb-[1px] mr-[4px]">
                        <QIcon_coupon width={12} height={12} color={COLORS.BLUE500} />
                      </div>
                      <span className="">
                        {' '}
                        {t(n_pricing.label.coupon_autumn(item?.available_discount_coupon?.amount?.formatted))}
                      </span>
                    </div>
                  )}

                  {/* 프로모션 할인 */}
                  {item?.bonuses.is_promotional && (
                    <div className="whitespace-nowrap px-[6px] rounded-[4px] bg-blue-50 text-blue-500 Caption-12Bd border-blue-200 border-1 flex items-center justify-center pt-[1px]">
                      {isVarationA
                        ? t(n_pricing.label.promo)
                        : getPromotionGapWonLabel(item?.total_discount_amount.value)}
                    </div>
                  )}
                  {/* 첫 구매 혜택 */}
                  {firstPurchaseBenefitCount > 0 && (
                    <div className="px-[6px] rounded-[4px] bg-red-50 text-red-500 Caption-12Bd border-red-200 border-1 flex items-center justify-center pt-[1px]">
                      <div className="mr-[4px]">
                        <QIcon_clock width={12} height={12} color={COLORS.RED500} />
                      </div>
                      <span className="whitespace-nowrap">{t(n_pricing.label.firt_buy)}</span>
                      <div className="block sm:hidden border-r-1 h-[8px] mx-[4px] border-red-200"></div>
                      <div className="block sm:hidden">
                        <DdayCounter
                          initialDay={new Date()}
                          targetDay={
                            new Date(
                              currentUser
                                ? add24Hours(currentUser?.braze?.private?.signup_date)
                                : add24Hours(new Date())
                            )
                          }
                          types={[D_DAY_TYPE.HOUR, D_DAY_TYPE.MIN, D_DAY_TYPE.SEC]}
                          classNameTotalContainer="flex flex-row items-center mb-[0px]"
                          classNameDigitContainer={classNames(
                            'text-red-500 Caption-12Bd flex flex-row items-center justify-center gap-x-[0px]'
                          )}
                          seperator={
                            <div
                              className={classNames(
                                'text-red-500 Caption-12Bd justify-center flex flex-col items-center'
                              )}
                            >
                              :
                            </div>
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div
              className={classNames('mt-[31px] flex flex-col relative', {
                'mt-[48px]': isMobile,
              })}
            >
              <div className="flex justify-end">
                {/* 월단가 상단 */}
                {item.discount_rate > 0 && (
                  <div className="Body2-14Md mr-[8px] text-gray-400 line-through decoration-red-500">
                    {item.prices.list_price.formatted}
                  </div>
                )}
                <div className="Body2-14Md text-gray-700">{item.prices.sale_price.formatted}</div>
              </div>
              <div className="flex flex-col relative">
                <div className="flex flex-row items-center justify-end">
                  {/* 월 단가 좌측 */}
                  {item.discount_rate > 0 ? (
                    <div className="Body1-16Bd mr-[4px] text-red-500">
                      {item.discount_rate}%<span className="Body2-14Bd">↓</span>
                    </div>
                  ) : null}
                  <div className="flex items-center">
                    <div className="flex flex-row items-center">
                      {/* 월 단가 */}
                      <div className={'Headline2-24Bd text-black flex items-center'}>
                        {t(n_pricing.four_week(item.prices.price_per_month.formatted))}
                      </div>
                    </div>
                  </div>
                </div>
                {/* 다른 나라 통화 선택시 월단가 하단 */}
                <div className="Body2-14Md text-gray-500 flex justify-end absolute bottom-[-22px] right-0">
                  {item.prices.price_per_lesson.equivalent
                    ? `(${isLocaleKO ? '월 ' : ''}${item.prices.price_per_lesson.equivalent.formatted})`
                    : ''}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-[48px] border-t-1 border-b-1 border-gray-100 mx-[16px] pt-[16px] pb-[16px] flex flex-col gap-y-[8px]">
            {/* 1:1 수업 */}
            <div className="flex justify-between items-center">
              <div className="flex flex-row items-center">
                <div className="mr-[8px]">
                  <img
                    src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1718603214332_tutor_icon_gray.webp'}
                    alt="lesson_icon"
                    className="w-[24px] h-[24px]"
                  />
                </div>
                <span className="Body2-14Md text-gray-700 whitespace-nowrap">
                  {t(duration.number == 40 ? n_pricing.benefit.lesson_40 : n_pricing.benefit.lesson_20)}
                </span>
              </div>

              <div className="flex flex-row items-center Body2-14Bd whitespace-nowrap">
                {t(n_pricing.credit_number_count(item.coupon_count))}
                {firstPurchaseBenefitCount > 0 && (
                  <div className={'ml-[4px] text-red-500 flex flex-row items-center h-[22px]'}>
                    <div className="">{t(n_pricing.twentyfour_hour.add_on(firstPurchaseBenefitCount))}</div>
                    <div className="self-start ml-[2px]">
                      <QIcon_clock width={12} height={12} color={COLORS.RED500} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* AI 스피킹 파트너 */}
            {conversationCreditCounts > 0 && (
              <div className="flex justify-between items-center">
                <div className="flex flex-row items-center">
                  <div className="mr-[8px]">
                    <img
                      src={
                        'https://d38emex6h5e12i.cloudfront.net/2024-image/1718603232900_ai_speaking_partner_gray.webp'
                      }
                      alt="lesson_icon"
                      className="w-[24px] h-[24px]"
                    />
                  </div>
                  <span className="Body2-14Md text-gray-700">{t(n_pricing.benefit.ai_speak)}</span>
                </div>

                <div className="Body2-14Bd">
                  {t(
                    n_pricing.credit_number_count(
                      completionConversationCreditBonusCount > 0
                        ? `${conversationCreditCounts}+${completionConversationCreditBonusCount}`
                        : conversationCreditCounts
                    )
                  )}
                </div>
              </div>
            )}

            {/* 스터디 클럽 */}
            {item?.bonuses.is_promotional && item?.bonuses.study_club_benefit && (
              <div className="flex justify-between items-center">
                <div className="flex flex-row items-center">
                  <div className="mr-[8px]">
                    <img
                      src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1718603248095_person_gray.webp'}
                      alt="lesson_icon"
                      className="w-[24px] h-[24px]"
                    />
                  </div>
                  <span className="Body2-14Md text-gray-700">{t(n_pricing.benefit.study_club)}</span>
                </div>
                <div className="Body2-14Bd">{t(n_pricing.credit_number_count(8))}</div>
              </div>
            )}
          </div>
          <div className="mt-[16px]">
            {/* 프로모션 추가 수강기간 */}
            {item.bonuses.active_days_bonus > 0 && (
              <div className="mt-[8px] mx-[16px] flex justify-between">
                <div className="flex flex-row items-center">
                  <div className="mr-[8px]">
                    <img
                      src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1718760520791_plus_gray.webp'}
                      alt="plus_gray"
                      className="w-[24px] h-[24px]"
                    />
                  </div>
                  <span className="Body2-14Md text-gray-700">{t(n_pricing.benefit.bonus_period)}</span>
                </div>
                <div className="Body2-14Bd">{t(n_pricing.benefit.day(item?.bonuses.active_days_bonus))}</div>
              </div>
            )}
            {/* 수업 연기 */}
            {item.is_weekly_lesson_package && deductionGraceCount > 0 && (
              <div className="mt-[8px] mx-[16px] flex justify-between">
                <div className="flex flex-row items-center">
                  <div className="mr-[8px]">
                    <img
                      src={'https://d38emex6h5e12i.cloudfront.net/2024-image/1718696557603_calendar_clock.webp'}
                      alt="calender_clock"
                      className="w-[24px] h-[24px]"
                    />
                  </div>
                  <span className="Body2-14Md text-gray-700">{t(n_pricing.benefit.delay)}</span>
                </div>
                <div className="Body2-14Bd">{t(n_pricing.deferral_number(deductionGraceCount))}</div>
              </div>
            )}
            {/* 튜터 먼저 예약 */}
            {item?.include_booking_priority_benefit && (
              <div className="mt-[8px] mx-[16px]">
                <div className="flex flex-row items-center">
                  <div className="mr-[8px]">
                    <QIcon_calendar_filled width={24} height={24} color={COLORS.GRAY400} />{' '}
                  </div>
                  <span className="Body2-14Md text-gray-700">{t(n_pricing.benefit.book_first)}</span>
                </div>
              </div>
            )}
          </div>

          <button
            className={classNames(
              'absolute bottom-[16px] mx-[16px] rounded-[7px] bg-purple-500 py-[12px] w-[calc(100%_-_32px)]'
            )}
            onClick={(e) => {
              hackleTrack({
                key: STUDENT_BTN_ID.HACKLE_CLICK_PURCHASE_BUY,
                properties: {
                  credit_span: duration.value,
                  credit_average: lessonNum.value,
                  credit_routine: packageOption.value,
                  number: item.coupon_count,
                  device_type: isApp ? 'app' : isMobile ? 'mobile_web' : 'pc',
                  page_id: currentUser ? 'portal_pricing' : 'landing_pricing',
                },
              })

              sendGTMEvent(STUDENT_BTN_ID.CLICK_PURCHASE_BUY, {
                credit_span: duration.value,
                credit_average: lessonNum.value,
                credit_routine: packageOption.value,
                number: item.coupon_count,
                device_type: isApp ? 'app' : isMobile ? 'mobile_web' : 'pc',
                page_id: currentUser ? 'portal_pricing' : 'landing_pricing',
              })
              handlePurchase(e)
            }}
          >
            <div className="Body2-14Bd text-white">{t(s_menu.portal.purchase_lessons)}</div>
          </button>
        </div>
      </div>
    )
    // .___  ___.   ______   .______    __   __       _______
    // |   \/   |  /  __  \  |   _  \  |  | |  |     |   ____|
    // |  \  /  | |  |  |  | |  |_)  | |  | |  |     |  |__
    // |  |\/|  | |  |  |  | |   _  <  |  | |  |     |   __|
    // |  |  |  | |  `--'  | |  |_)  | |  | |  `----.|  |____
    // |__|  |__|  \______/  |______/  |__| |_______||_______|
  } else if (isMobile) {
    return (
      <div
        key={`${item.id}`}
        className={classNames(
          'relative rounded-[8px] bg-white drop-shadow-[0px_2px_8px_rgba(0,0,0,0.15)] sm:mt-[24px]',
          {
            'border-[2px] border-purple-700': salesCoupon,
          }
        )}
      >
        {salesCoupon && (
          <div className={'rounded-t-[4px] bg-purple-700 py-[4px] pl-[12px]'}>
            <div className="Body2-14Bd text-white">{t(s_pricing.card_popular)}</div>
          </div>
        )}

        <div
          className={classNames('relative px-[20px] pt-[20px]', {
            'min-w-[320px]': isMobile,
          })}
        >
          {getAdditionalDiscount(item) ? (
            <div className="flex mb-[12px] min-h-[21px]">
              <div className="Caption-12Bd text-red-500 rounded-[4px] border-1 px-[6px] border-red-200 bg-red-50">
                {t(n_pricing.b2b2c_label.sale(getAdditionalDiscount(item)))}
              </div>
            </div>
          ) : getCompletionBonus(item) ? (
            <div className="flex mb-[12px] min-h-[21px]">
              <div className="Caption-12Bd text-red-500 rounded-[4px] border-1 px-[6px] border-red-200 bg-red-50">
                {t(n_pricing.b2b2c_label.coupon_added(getCompletionBonus(item)))}
              </div>
            </div>
          ) : (
            <div className="flex mb-[12px] min-h-[21px]"></div>
          )}

          <div
            className={classNames('relative flex flex-col', {
              'mt-[16px]': (item.available_discount_coupon && isMobile) || (isMobile && item.coupon_count == 12),
            })}
          >
            <div className={'Title1-20Bd relative mt-[3px] flex text-black'}>
              <div className="relative flex flex-row items-center">
                <div className=""> {t(n_pricing.credit_number_count(item.coupon_count))}</div>
                {/* B2C 첫구매 보너스 */}
                {firstPurchaseBenefitCount > 0 && (
                  <div
                    className={classNames('Title1-20Bd absolute ml-[4px] text-red-400 flex flex-row items-center', {
                      'right-[-184px] top-[-1px]': isLocaleKO,
                      'right-[-170px] top-[-2px]': !isLocaleKO,
                    })}
                  >
                    <div className="pt-[1px]">{t(n_pricing.twentyfour_hour.add_on(firstPurchaseBenefitCount))}</div>
                    <div className="mb-[1px] min-w-[130px] ml-[4px] flex flex-row whitespace-nowrap items-center text-red-500 px-[6px] py-[2px] bg-red-100 rounded-[12px] mt-[1px]">
                      <QIcon_clock width={16} height={16} color={COLORS.RED500} />
                      <div className="Caption-12Bd mx-[4px]">{t(n_pricing.ended_in)} </div>
                      <div className="">
                        <DdayCounter
                          initialDay={new Date()}
                          targetDay={new Date(expiredAt)}
                          types={[D_DAY_TYPE.HOUR, D_DAY_TYPE.MIN, D_DAY_TYPE.SEC]}
                          classNameTotalContainer="flex flex-row items-center mb-[0px]"
                          classNameDigitContainer={classNames(
                            'Caption-12Bd flex flex-row items-center justify-center gap-x-[0px] pb-[0px]'
                          )}
                          seperator={
                            <div className={classNames('Caption-12Bd justify-center flex flex-col items-center')}>
                              :
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
                {/* B2B2C 완강보너스 */}
                {completionBenefitCount > 0 && (
                  <div className={classNames('Title1-20Bd ml-[4px] text-blue-500 flex flex-row items-start')}>
                    <div className="pt-[1px]">{t(n_pricing.twentyfour_hour.add_on(completionBenefitCount))}</div>
                    <div className="text-blue-500 Caption-12Md ml-[4px]">{t(n_pricing.benefit_b2b2c.title)}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="Body2-14Md mt-[4px] text-gray-500 border-b-1 pb-[20px]">
              <span className="mr-[4px]"> {t(n_pricing.lesson_span)}</span>
              {packageOption.value === 'normal' ? (
                t(s_pricing.day(item.exp_dates))
              ) : (
                <span>
                  {t(s_portal_home.default_stat.week(item.exp_weeks))}
                  <span className="ml-[4px]">({t(s_pricing.day(item.exp_dates))})</span>
                </span>
              )}
            </div>
          </div>
          <div
            className={classNames('flex flex-col items-end', {
              'mt-[48px]': isMobile,
            })}
          >
            <div className="flex flex-col">
              <div className="flex flex-row items-center justify-end relative">
                {/* B2B 프로모션 할인가 */}
                {item.bonuses.is_promotional && (
                  <div className="absolute right-[0px] top-[-28px] rounded-[4px] Caption-12Bd py-[2px] px-[4px] bg-red-100 text-red-500">
                    {t(n_pricing.benefit_b2b2c.label_promo)}
                  </div>
                )}
                <div className="right-0 top-[-28px] absolute">
                  {hasAvailableCoupon && (
                    <div className="flex flex-row rounded-[4px] px-[4px] py-[2px] bg-red-100 text-red-700 Caption-12Bd gap-[4px] items-center">
                      <QIcon_coupon width={20} height={20} color={COLORS.RED400} />
                      {t(
                        n_purchase.upsell_coupon.pricing_card_badge(item?.available_discount_coupon?.amount?.formatted)
                      )}
                    </div>
                  )}
                </div>
                {item.discount_rate ? (
                  <div className="Body1-16Bd mr-[4px] text-red-500">{item.discount_rate}%</div>
                ) : null}
                <div className="flex items-end">
                  <div className="flex flex-row items-end">
                    <div className={'Headline1-28Bd text-black'}>{item.prices?.price_per_lesson?.formatted}</div>
                  </div>

                  <div
                    className={classNames('Body2-14Md text-black ml-[2px] pb-[4px]', {
                      'Body2-14Md': isMobile,
                    })}
                  >
                    {t(s_pricing.by_one)}
                  </div>
                </div>
              </div>
              <div className="Body2-14Md text-gray-500 flex justify-end">
                {item.prices.price_per_lesson.equivalent
                  ? `(${item.prices.price_per_lesson.equivalent.formatted})`
                  : ''}
              </div>
            </div>
            <div className="mt-[4px] flex justify-end">
              <div className="Body2-14Md mr-[8px] text-gray-400 line-through">{item.prices.list_price.formatted}</div>
              <div className="Body2-14Md text-gray-700">{item.prices.sale_price.formatted}</div>
            </div>
            <button
              className={classNames(
                'w-full rounded-[7px] bg-purple-700 py-[12px] px-[28px] sm:min-w-[81px] mb-[32px] h-[57px] mt-[24px]'
              )}
              onClick={(e) => {
                hackleTrack({
                  key: STUDENT_BTN_ID.HACKLE_CLICK_PURCHASE_BUY,
                  properties: {
                    credit_span: duration.value,
                    credit_average: lessonNum.value,
                    credit_routine: packageOption.value,
                    number: item.coupon_count,
                    device_type: isApp ? 'app' : isMobile ? 'mobile_web' : 'pc',
                    page_id: currentUser ? 'portal_pricing' : 'landing_pricing',
                  },
                })

                sendGTMEvent(STUDENT_BTN_ID.CLICK_PURCHASE_BUY, {
                  credit_span: duration.value,
                  credit_average: lessonNum.value,
                  credit_routine: packageOption.value,
                  number: item.coupon_count,
                  device_type: isApp ? 'app' : isMobile ? 'mobile_web' : 'pc',
                  page_id: currentUser ? 'portal_pricing' : 'landing_pricing',
                })
                handlePurchase(e)
              }}
            >
              <div className="Body2-14Bd text-white">{t(s_menu.portal.purchase_lessons)}</div>
              <div className="Caption-12Md text-purple-100">{t(n_pricing.pay_benefit_1)}</div>
            </button>
          </div>
        </div>
        <div className="Body2-14Md rounded-b-[6px] px-[20px] pb-[32px] text-gray-700">
          <NormalDetailContent item={item} />
        </div>
        {openUpSellingBottomSheet && (
          <DSBottomSheet
            id="upselling_bottom_sheet"
            isOpen={openUpSellingBottomSheet}
            setIsOpen={setOpenUpSellingBottomSheet}
            closeButton={true}
          >
            <UpSellingPopupBody
              countryCode={countryCode}
              selectedItem={item}
              productsGroup={productsGroup}
              refetch={refetchProductLine}
            />
          </DSBottomSheet>
        )}
      </div>
    )
  } else {
    return (
      <div
        key={`${item.id}`}
        className={classNames(
          'relative mt-[32px] rounded-[8px] bg-white drop-shadow-[0px_1px_4px_rgba(0,0,0,0.15)] sm:mt-[24px] min-h-[620px]',
          {
            'border-[2px] border-purple-700': salesCoupon,
            'border-[2px] border-transparent': !salesCoupon,
          }
        )}
      >
        {/* 최상단 인기 / 추천 배너 */}
        {salesCoupon && (
          <div className={'absolute w-full rounded-t-[4px] text-center bg-purple-700 py-[4px]'}>
            <div className="Body2-14Md text-white">{t(s_pricing.card_popular)}</div>
          </div>
        )}
        <div className="relative flex-col px-[20px] pt-[46px] flex h-[316px] min-w-[282px]">
          {getAdditionalDiscount(item) ? (
            <div className="flex mb-[12px] min-h-[21px]">
              <div className="Caption-12Bd text-red-500 rounded-[4px] border-1 px-[6px] border-red-200 bg-red-50">
                {t(n_pricing.b2b2c_label.sale(getAdditionalDiscount(item)))}
              </div>
            </div>
          ) : getCompletionBonus(item) ? (
            <div className="flex mb-[12px] min-h-[21px]">
              <div className="Caption-12Bd text-red-500 rounded-[4px] border-1 px-[6px] border-red-200 bg-red-50">
                {t(n_pricing.b2b2c_label.coupon_added(getCompletionBonus(item)))}
              </div>
            </div>
          ) : (
            <div className="flex mb-[12px] min-h-[21px]"></div>
          )}

          {/* 상단부 수강횟수, 수업일수 */}
          <div
            className={classNames('relative flex flex-col', {
              'mt-[16px]':
                (item.available_discount_coupon && isMobile) ||
                (activeId == PRICING_TAB.PROMOTION && isMobile) ||
                (isMobile && item.coupon_count == 12),
            })}
          >
            <div className="flex">
              <div className={'Title1-20Bd relative mt-[3px] flex text-black items-center'}>
                {t(n_pricing.credit_number_count(item.coupon_count))}
                {firstPurchaseBenefitCount > 0 && (
                  <div
                    className={classNames('Title1-20Bd absolute ml-[4px] text-red-400 flex flex-row items-center', {
                      'right-[-78px] top-[-1px]': isLocaleKO,
                      'right-[-64px]': !isLocaleKO,
                    })}
                  >
                    <div className="pt-[1px]">{t(n_pricing.twentyfour_hour.add_on(firstPurchaseBenefitCount))}</div>
                    <div className="ml-[4px] p-[4px] bg-red-200 rounded-[12px]">
                      <QIcon_clock width={16} height={16} color={COLORS.RED500} />
                    </div>
                  </div>
                )}
                {/* B2B2C 완강보너스 */}
                {completionBenefitCount > 0 && (
                  <div className={classNames('Title1-20Bd ml-[4px] text-blue-500 flex flex-row items-start')}>
                    <div className="pt-[1px]">{t(n_pricing.twentyfour_hour.add_on(completionBenefitCount))}</div>
                    <div className="text-blue-500 Caption-12Md ml-[4px]">{t(n_pricing.benefit_b2b2c.title)}</div>
                  </div>
                )}
              </div>
            </div>
            <div className="Body2-14Md mt-[4px] text-gray-700 border-b-1 pb-[20px]">
              <span className="mr-[4px]"> {t(n_pricing.lesson_span)}</span>
              {packageOption.value === 'normal' ? (
                t(s_pricing.day(item.exp_dates))
              ) : (
                <span>
                  {t(s_portal_home.default_stat.week(item.exp_weeks))}({t(s_pricing.day(item.exp_dates))})
                </span>
              )}
            </div>
          </div>

          {/* 중단부 할인 내용, 가격 */}
          <div
            className={classNames('mt-[50px] mb-[24px] flex flex-col relative', {
              'mt-[48px]': isMobile,
            })}
          >
            <div className="flex flex-col relative">
              {/* B2B 프로모션 할인가 */}
              {item.bonuses.is_promotional && (
                <div className="absolute right-[0px] top-[-28px] rounded-[4px] Caption-12Bd py-[2px] px-[4px] bg-red-100 text-red-500">
                  {t(n_pricing.benefit_b2b2c.label_promo)}
                </div>
              )}
              <div className="right-0 top-[-28px] absolute ">
                {hasAvailableCoupon && (
                  <div className="flex flex-row rounded-[4px] px-[4px] py-[2px] bg-red-100 text-red-700 Caption-12Bd gap-[4px] items-center">
                    <QIcon_coupon width={20} height={20} color={COLORS.RED400} />
                    {t(n_purchase.upsell_coupon.pricing_card_badge(item?.available_discount_coupon?.amount?.formatted))}
                  </div>
                )}
              </div>
              <div className="flex flex-row items-center justify-end">
                {item.discount_rate ? (
                  <div className="Title2-18Bd mr-[4px] text-red-400">{item.discount_rate}%</div>
                ) : null}
                <div className="flex items-end">
                  <div className="flex flex-row items-end">
                    <div className={'Headline1-28Bd text-black'}>{item.prices.price_per_lesson.formatted}</div>
                  </div>

                  <div
                    className={classNames('Body2-14Md text-black ml-[2px] pb-[4px]', {
                      'Body2-14Md': isMobile,
                    })}
                  >
                    {t(s_pricing.by_one)}
                  </div>
                </div>
              </div>
              <div className="Body2-14Md text-gray-500 flex justify-end">
                {item.prices.price_per_lesson.equivalent
                  ? `(${item.prices.price_per_lesson.equivalent.formatted})`
                  : ''}
              </div>
            </div>
            <div className="mt-[4px] flex justify-end">
              <div className="Body2-14Md mr-[8px] text-gray-400 line-through">{item.prices.list_price.formatted}</div>
              <div className="Body2-14Md text-gray-700">{item.prices.sale_price.formatted}</div>
            </div>
            <button
              className={classNames('absolute mt-[0px]  w-full rounded-[7px] bg-purple-700 py-[12px] bottom-[-70px]', {
                'mt-[16px]': isMobile,
              })}
              onClick={(e) => {
                hackleTrack({
                  key: STUDENT_BTN_ID.HACKLE_CLICK_PURCHASE_BUY,
                  properties: {
                    credit_span: duration.value,
                    credit_average: lessonNum.value,
                    credit_routine: packageOption.value,
                    number: item.coupon_count,
                    device_type: isApp ? 'app' : isMobile ? 'mobile_web' : 'pc',
                    page_id: currentUser ? 'portal_pricing' : 'landing_pricing',
                  },
                })

                sendGTMEvent(STUDENT_BTN_ID.CLICK_PURCHASE_BUY, {
                  credit_span: duration.value,
                  credit_average: lessonNum.value,
                  credit_routine: packageOption.value,
                  number: item.coupon_count,
                  device_type: isApp ? 'app' : isMobile ? 'mobile_web' : 'pc',
                  page_id: currentUser ? 'portal_pricing' : 'landing_pricing',
                })
                handlePurchase(e)
              }}
            >
              <div className="Body2-14Bd text-white">{t(s_menu.portal.purchase_lessons)}</div>
            </button>
          </div>
        </div>
        <div className="h-[208px]">
          <div
            className={classNames('Body2-14Md rounded-b-[6px] border-gray-200 px-[20px] text-gray-700', {
              'pt-[32px]': item.prices.price_per_lesson.equivalent,
              'py-[18px]': !item.prices.price_per_lesson.equivalent,
              'mt-[8px]': !isLocaleKO,
              'mt-[24px]': isLocaleKO,
            })}
          >
            <NormalDetailContent item={item} />
          </div>
        </div>
      </div>
    )
  }
}

export default ProductCard
